import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  updateDoc, 
  query,
  where,
  onSnapshot,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { getFirebaseDB } from './firebase';
import { 
  Subscription, 
  SubscriptionType, 
  SubscriptionStatus, 
  SubscriptionLimits,
  SubscriptionUsage 
} from '../types/subscription';
import { getSubscriptionLimits } from '../constants/subscription';

const db = getFirebaseDB();

export class SubscriptionService {
  private static instance: SubscriptionService;

  static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  // Créer un abonnement gratuit pour un nouvel utilisateur
  async createFreeSubscription(userId: string): Promise<Subscription> {
    const limits = getSubscriptionLimits(SubscriptionType.FREE);
    
    const subscription: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'> = {
      userId,
      type: SubscriptionType.FREE,
      status: SubscriptionStatus.ACTIVE,
      startDate: new Date(),
      limits,
      usage: {
        currentPets: 0,
        currentLogs: 0,
        currentAiChats: 0,
        currentReminders: 0,
        lastResetDate: new Date()
      }
    };

    const docRef = doc(db, 'subscriptions', userId);
    await setDoc(docRef, {
      ...subscription,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      startDate: Timestamp.fromDate(subscription.startDate),
      usage: {
        ...subscription.usage,
        lastResetDate: Timestamp.fromDate(subscription.usage.lastResetDate)
      }
    }, { merge: true });

    return {
      id: docRef.id,
      ...subscription,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Récupérer l'abonnement d'un utilisateur
  async getUserSubscription(userId: string): Promise<Subscription | null> {
    const toDateSafe = (v: any): Date => {
      try {
        if (!v) return new Date();
        if (typeof v.toDate === 'function') return v.toDate();
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? new Date() : d;
      } catch {
        return new Date();
      }
    };

    const shouldKeepSubscription = (data: any): boolean => {
      const status = data?.status as SubscriptionStatus | undefined;
      if (status === SubscriptionStatus.ACTIVE) return true;

      if (status === SubscriptionStatus.CANCELLED) {
        const endDate = data?.endDate ? toDateSafe(data.endDate) : undefined;
        if (endDate && endDate.getTime() > Date.now()) return true;
      }

      return false;
    };

    // Preferred format: deterministic doc id = userId
    const deterministicRef = doc(db, 'subscriptions', userId);
    const deterministicSnap = await getDoc(deterministicRef);

    if (deterministicSnap.exists()) {
      const data = deterministicSnap.data() as any;
      if (!shouldKeepSubscription(data)) {
        return null;
      }

      return {
        id: deterministicSnap.id,
        userId: data.userId,
        type: data.type,
        status: data.status,
        startDate: toDateSafe(data.startDate),
        endDate: data.endDate ? toDateSafe(data.endDate) : undefined,
        cancelAtPeriodEnd: typeof data.cancelAtPeriodEnd === 'boolean' ? data.cancelAtPeriodEnd : undefined,
        cancelAt: data.cancelAt ? toDateSafe(data.cancelAt) : undefined,
        stripeCustomerId: data.stripeCustomerId,
        stripeSubscriptionId: data.stripeSubscriptionId,
        limits: data.limits,
        usage: {
          ...data.usage,
          lastResetDate: toDateSafe(data.usage?.lastResetDate)
        },
        createdAt: toDateSafe(data.createdAt),
        updatedAt: toDateSafe(data.updatedAt)
      };
    }

    // Legacy format fallback: random doc ids with userId field
    const legacyQuery = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', '==', SubscriptionStatus.ACTIVE)
    );
    const legacySnap = await getDocs(legacyQuery);
    if (legacySnap.empty) return null;

    const legacyDoc = legacySnap.docs[0];
    const legacyData = legacyDoc.data() as any;

    // Migrate legacy doc to deterministic id so listeners + future updates work reliably
    try {
      await setDoc(deterministicRef, {
        ...legacyData,
        userId
      }, { merge: true });

      const migratedSnap = await getDoc(deterministicRef);
      if (migratedSnap.exists()) {
        const data = migratedSnap.data() as any;
        if (!shouldKeepSubscription(data)) {
          return null;
        }

        return {
          id: migratedSnap.id,
          userId: data.userId,
          type: data.type,
          status: data.status,
          startDate: toDateSafe(data.startDate),
          endDate: data.endDate ? toDateSafe(data.endDate) : undefined,
          cancelAtPeriodEnd: typeof data.cancelAtPeriodEnd === 'boolean' ? data.cancelAtPeriodEnd : undefined,
          cancelAt: data.cancelAt ? toDateSafe(data.cancelAt) : undefined,
          stripeCustomerId: data.stripeCustomerId,
          stripeSubscriptionId: data.stripeSubscriptionId,
          limits: data.limits,
          usage: {
            ...data.usage,
            lastResetDate: toDateSafe(data.usage?.lastResetDate)
          },
          createdAt: toDateSafe(data.createdAt),
          updatedAt: toDateSafe(data.updatedAt)
        };
      }
    } catch {
      // ignore migration failures and fall back to legacy doc
    }

    if (!shouldKeepSubscription(legacyData)) return null;

    return {
      id: legacyDoc.id,
      userId: legacyData.userId,
      type: legacyData.type,
      status: legacyData.status,
      startDate: toDateSafe(legacyData.startDate),
      endDate: legacyData.endDate ? toDateSafe(legacyData.endDate) : undefined,
      cancelAtPeriodEnd: typeof legacyData.cancelAtPeriodEnd === 'boolean' ? legacyData.cancelAtPeriodEnd : undefined,
      cancelAt: legacyData.cancelAt ? toDateSafe(legacyData.cancelAt) : undefined,
      stripeCustomerId: legacyData.stripeCustomerId,
      stripeSubscriptionId: legacyData.stripeSubscriptionId,
      limits: legacyData.limits,
      usage: {
        ...legacyData.usage,
        lastResetDate: toDateSafe(legacyData.usage?.lastResetDate)
      },
      createdAt: toDateSafe(legacyData.createdAt),
      updatedAt: toDateSafe(legacyData.updatedAt)
    };
  }

  // Mettre à jour l'utilisation des fonctionnalités
  async updateUsage(userId: string, usageUpdate: Partial<SubscriptionUsage>): Promise<void> {
    const subscription = await this.getUserSubscription(userId);
    if (!subscription) return;

    const docRef = doc(db, 'subscriptions', subscription.id);
    await updateDoc(docRef, {
      usage: {
        ...subscription.usage,
        ...usageUpdate,
        lastResetDate: Timestamp.fromDate(new Date())
      },
      updatedAt: serverTimestamp()
    });
  }

  // Vérifier si l'utilisateur peut effectuer une action
  async canPerformAction(
    userId: string, 
    action: 'addPet' | 'addLog' | 'useAiChat' | 'addReminder'
  ): Promise<{ allowed: boolean; reason?: string; currentUsage?: number; limit?: number }> {
    const subscription = await this.getUserSubscription(userId);
    
    if (!subscription) {
      // Créer un abonnement gratuit si aucun n'existe
      await this.createFreeSubscription(userId);
      
      // Ajouter un petit délai pour la cohérence de Firestore
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const newSubscription = await this.getUserSubscription(userId);
      if (!newSubscription) return { allowed: false, reason: 'Impossible de créer un abonnement' };
      
      return this.checkLimits(newSubscription, action);
    }

    return this.checkLimits(subscription, action);
  }

  private checkLimits(subscription: Subscription, action: string): { allowed: boolean; reason?: string; currentUsage?: number; limit?: number } {
    const { limits, usage } = subscription;

    switch (action) {
      case 'addPet':
        if (usage.currentPets >= limits.maxPets) {
          return {
            allowed: false,
            reason: `Limite d'animaux atteinte (${usage.currentPets}/${limits.maxPets})`,
            currentUsage: usage.currentPets,
            limit: limits.maxPets
          };
        }
        break;

      case 'addLog':
        if (usage.currentLogs >= limits.maxLogs) {
          return {
            allowed: false,
            reason: `Limite de logs atteinte (${usage.currentLogs}/${limits.maxLogs})`,
            currentUsage: usage.currentLogs,
            limit: limits.maxLogs
          };
        }
        break;

      case 'useAiChat':
        if (usage.currentAiChats >= limits.maxAiChats) {
          return {
            allowed: false,
            reason: `Limite de conversations IA atteinte (${usage.currentAiChats}/${limits.maxAiChats})`,
            currentUsage: usage.currentAiChats,
            limit: limits.maxAiChats
          };
        }
        break;

      case 'addReminder':
        if (usage.currentReminders >= limits.maxReminders) {
          return {
            allowed: false,
            reason: `Limite de rappels atteinte (${usage.currentReminders}/${limits.maxReminders})`,
            currentUsage: usage.currentReminders,
            limit: limits.maxReminders
          };
        }
        break;

      default:
        return { allowed: false, reason: 'Action non reconnue' };
    }

    return { allowed: true };
  }

  // Écouter les changements d'abonnement en temps réel
  onSubscriptionChange(userId: string, callback: (subscription: Subscription | null) => void) {
    const docRef = doc(db, 'subscriptions', userId);

    return onSnapshot(docRef, (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }

      const data = snap.data() as any;

      const toDateSafe = (v: any): Date => {
        try {
          if (!v) return new Date();
          if (typeof v.toDate === 'function') return v.toDate();
          const d = new Date(v);
          return Number.isNaN(d.getTime()) ? new Date() : d;
        } catch {
          return new Date();
        }
      };

      const status = data?.status as SubscriptionStatus | undefined;
      const endDate = data?.endDate ? toDateSafe(data.endDate) : undefined;

      const shouldKeepSubscription = (() => {
        if (status === SubscriptionStatus.ACTIVE) return true;
        if (status === SubscriptionStatus.CANCELLED) {
          if (endDate && endDate.getTime() > Date.now()) return true;
        }
        return false;
      })();

      if (!shouldKeepSubscription) {
        callback(null);
        return;
      }
      
      const subscription: Subscription = {
        id: snap.id,
        userId: data.userId,
        type: data.type,
        status: data.status,
        startDate: toDateSafe(data.startDate),
        endDate,
        cancelAtPeriodEnd: typeof data.cancelAtPeriodEnd === 'boolean' ? data.cancelAtPeriodEnd : undefined,
        cancelAt: data.cancelAt ? toDateSafe(data.cancelAt) : undefined,
        stripeCustomerId: data.stripeCustomerId,
        stripeSubscriptionId: data.stripeSubscriptionId,
        limits: data.limits,
        usage: {
          ...data.usage,
          lastResetDate: toDateSafe(data.usage?.lastResetDate)
        },
        createdAt: toDateSafe(data.createdAt),
        updatedAt: toDateSafe(data.updatedAt)
      };

      callback(subscription);
    });
  }

  // Créer un abonnement invité (pour les administrateurs)
  async createGuestSubscription(userId: string): Promise<Subscription> {
    const limits = getSubscriptionLimits(SubscriptionType.GUEST);
    
    const subscription: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'> = {
      userId,
      type: SubscriptionType.GUEST,
      status: SubscriptionStatus.ACTIVE,
      startDate: new Date(),
      limits,
      usage: {
        currentPets: 0,
        currentLogs: 0,
        currentAiChats: 0,
        currentReminders: 0,
        lastResetDate: new Date()
      }
    };

    const docRef = doc(db, 'subscriptions', userId);
    await setDoc(docRef, {
      ...subscription,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      startDate: Timestamp.fromDate(subscription.startDate),
      usage: {
        ...subscription.usage,
        lastResetDate: Timestamp.fromDate(subscription.usage.lastResetDate)
      }
    }, { merge: true });

    return {
      id: docRef.id,
      ...subscription,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  // Mettre à jour le type d'abonnement (pour Stripe)
  async updateSubscriptionType(
    userId: string, 
    newType: SubscriptionType, 
    stripeData?: {
      customerId?: string;
      subscriptionId?: string;
    }
  ): Promise<void> {
    const subscription = await this.getUserSubscription(userId);
    if (!subscription) throw new Error('Aucun abonnement trouvé');

    const docRef = doc(db, 'subscriptions', subscription.id);
    const newLimits = getSubscriptionLimits(newType);

    await updateDoc(docRef, {
      type: newType,
      limits: newLimits,
      stripeCustomerId: stripeData?.customerId,
      stripeSubscriptionId: stripeData?.subscriptionId,
      updatedAt: serverTimestamp()
    });
  }
}

export const subscriptionService = SubscriptionService.getInstance();
